import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react'
import { imageUrl, parseDay } from './data'

const dayFormat = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
const longDayFormat = new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })

export const formatDay = d => (d ? dayFormat.format(parseDay(d)) : '')
export const formatLongDay = d => (d ? longDayFormat.format(parseDay(d)) : '')
export const formatScore = n => (n == null ? '–' : n.toFixed(2))
export const formatSigned = (n, digits = 2) => {
  if (n == null) return ''
  const rounded = Number(n.toFixed(digits))
  if (rounded === 0) return (0).toFixed(digits)
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded).toFixed(digits)}`
}

// Rank movement: positive = climbed.
export function RankDelta({ value, size = 'md' }) {
  if (!value) {
    return (
      <span className={`hv-delta hv-delta--flat hv-delta--${size}`} aria-label="No change">
        <Minus size={12} strokeWidth={2.5} />
      </span>
    )
  }
  const up = value > 0
  const Icon = up ? ArrowUpRight : ArrowDownRight
  return (
    <span className={`hv-delta hv-delta--${up ? 'up' : 'down'} hv-delta--${size}`} aria-label={`${up ? 'Up' : 'Down'} ${Math.abs(value)}`}>
      <Icon size={size === 'lg' ? 16 : 12} strokeWidth={2.5} />
      {Math.abs(value)}
    </span>
  )
}

export function ScoreDelta({ value }) {
  if (value == null || Math.abs(value) < 0.005) return <span className="hv-score-delta hv-score-delta--flat">±0.00</span>
  return <span className={`hv-score-delta hv-score-delta--${value > 0 ? 'up' : 'down'}`}>{formatSigned(value)}</span>
}

export function TierChip({ tier, size = 'md' }) {
  return <span className={`hv-tier hv-tier--${tier ? tier.toLowerCase() : 'none'} hv-tier--${size}`}>{tier || '?'}</span>
}

export function TeamLogo({ team, size = 20 }) {
  if (!team?.logo) return <span className="hv-logo-fallback" style={{ width: size, height: size }} />
  return <img className="hv-logo" src={team.logo} alt={team.acronym} width={size} height={size} draggable="false" />
}

export function Avatar({ src, name, size = 32 }) {
  const url = imageUrl(src)
  const initials = name.split(' ').map(w => w[0]).slice(0, 2).join('')
  return (
    <span className="hv-avatar" style={{ width: size, height: size }}>
      {url ? <img src={url} alt="" loading="lazy" draggable="false" /> : <span>{initials}</span>}
    </span>
  )
}

const TIER_ORDER = { S: 5, A: 4, B: 3, C: 2, F: 1 }

// One-line description of an event, with the counterparty team when known.
export function describeEvent(event, league) {
  const other = event.team_id ? league.byId.get(event.team_id) : null
  switch (event.kind) {
    case 'in':
      if (other) return { verb: 'Acquired', rest: `from ${other.acronym}`, other }
      return { verb: event.trade ? 'Acquired' : 'Signed', rest: event.trade ? 'via trade' : '' }
    case 'out':
      if (other) return { verb: 'Traded', rest: `to ${other.acronym}`, other }
      return { verb: event.trade ? 'Traded away' : 'Released', rest: '' }
    case 'tier':
      if (!event.from_tier) return { verb: 'First ranked', rest: '' }
      if (!event.to_tier) return { verb: 'Ranking cleared', rest: '' }
      return { verb: TIER_ORDER[event.to_tier] > TIER_ORDER[event.from_tier] ? 'Upgraded' : 'Downgraded', rest: '' }
    default:
      return { verb: event.name, rest: '' }
  }
}

export function EventRow({ event, league, maxImpact }) {
  const { verb, rest, other } = describeEvent(event, league)
  const impact = event.impact
  const width = impact == null || !maxImpact ? 0 : Math.max(4, (Math.abs(impact) / maxImpact) * 100)
  return (
    <li className="hv-event">
      <Avatar src={event.image} name={event.name} size={34} />
      <div className="hv-event-text">
        <div className="hv-event-line">
          <span className={`hv-event-verb hv-event-verb--${event.kind}`}>{verb}</span>
          {event.kind !== 'note' && <span className="hv-event-name">{event.name}</span>}
        </div>
        <div className="hv-event-meta">
          {event.kind === 'tier' ? (
            <span className="hv-tier-move">
              <TierChip tier={event.from_tier} size="sm" />
              <span className="hv-arrow">→</span>
              <TierChip tier={event.to_tier} size="sm" />
            </span>
          ) : event.tier ? (
            <TierChip tier={event.tier} size="sm" />
          ) : null}
          {rest && (
            <span className="hv-event-rest">
              {other && <TeamLogo team={other} size={14} />}
              {rest}
            </span>
          )}
        </div>
      </div>
      <div className="hv-event-impact">
        {impact == null ? (
          <span className="hv-muted hv-small" title="Recorded before roster tracking, so the effect on the score is unknown">n/a</span>
        ) : (
          <>
            <span className={`hv-impact-value ${impact > 0 ? 'is-up' : impact < 0 ? 'is-down' : ''}`}>{formatSigned(impact)}</span>
            <span className="hv-impact-bar">
              <span className={impact >= 0 ? 'is-up' : 'is-down'} style={{ width: `${width}%` }} />
            </span>
          </>
        )}
      </div>
    </li>
  )
}
